import React, { useEffect, useMemo, useState } from 'react';

import { Locate, TextInput } from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { Dealer, DealersList } from '../../../../shared/typings/main.d';
import { onVWBasicDealerSearchClickEvent } from '../../../../shared/tagging/tagging-helpers';
import { StyledZipInputContainer, StyledInputWrapper, StyledLocationButton } from './styles';
import { getZipCodeFromCookies } from '../../../../shared/utils/cookies';

interface ZipInputViewProps {
  translationHandler: (key: string) => string;
  trackingManager: any;
  fetchDealersListByZipCode: (zipCode: string) => Promise<void>;
  getUserLocation: () => Promise<string | null>;
  isLoadingDealers: boolean;
  isLoadingPreferredDealer: boolean;
  userPreferredDealer?: Dealer;
  dealersList: DealersList[];
  isCanada: boolean;
}

export const ZipInputView = ({
  translationHandler,
  trackingManager,
  fetchDealersListByZipCode,
  getUserLocation,
  isLoadingDealers,
  isLoadingPreferredDealer,
  userPreferredDealer,
  isCanada,
}: ZipInputViewProps) => {
  const [manualError, setManualError] = useState<string | null>(null);
  const [hasManualError, setHasManualError] = useState<boolean>(false);

  const errorMessage: string = translationHandler('manageDealer.zipInput.error.message');

  const zipCodeSchema = Yup.object().shape({
    zipCode: isCanada 
      ? Yup.string()
        .matches(/^[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d$/, errorMessage)
        .required(errorMessage)
      : Yup.string()
        .matches(/^\d{5}$/, errorMessage)
        .required(errorMessage),
  });

  const initialZip = useMemo(() => {
    const preferredZip = userPreferredDealer?.address?.zip;
    if (preferredZip) return preferredZip;

    const cookieZip = getZipCodeFromCookies();
    return cookieZip || '';
  }, []);

  const formik = useFormik({
    initialValues: {
      zipCode: initialZip,
    },
    validationSchema: zipCodeSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!values.zipCode) return;
      await fetchDealersListByZipCode(values.zipCode);
    },
  });

  useEffect(() => {
    if (userPreferredDealer?.address?.zip) {
      const preferredZip = userPreferredDealer.address.zip;

      if (preferredZip !== formik.values.zipCode) {
        formik.setFieldValue('zipCode', preferredZip);

        setManualError(null);
        setHasManualError(false);
      }
    }
  }, [userPreferredDealer]);

  const trackDealerSearch = () => {
    try {
      onVWBasicDealerSearchClickEvent(trackingManager, {
        Name: 'Find a Dealer',
        StepName: 'Find a Dealer Map',
        StepNumber: '3',
      });
    } catch (error) {
      console.warn('Error tracking dealer search event:', error);
    }
  };

  const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    formik.handleChange(e);

    if (hasManualError) {
      setManualError(null);
      setHasManualError(false);
    }
  };

  const handleBlur = async (e: React.SyntheticEvent<HTMLInputElement>) => {
    formik.handleBlur(e);

    const errors = await formik.validateForm();

    if (!errors.zipCode) {
      setManualError(null);
      setHasManualError(false);
    }

    if (!errors.zipCode && formik.values.zipCode) {
      trackDealerSearch();
      await fetchDealersListByZipCode(formik.values.zipCode);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const errors = await formik.validateForm();

      if (!errors.zipCode) {
        setManualError(null);
        setHasManualError(false);
      }

      if (!errors.zipCode && formik.values.zipCode) {
        trackDealerSearch();
        await fetchDealersListByZipCode(formik.values.zipCode);
      } else {
        formik.setFieldTouched('zipCode', true);
      }
    }
  };

  const handleUseMyLocation = async () => {
    try {
      const detectedZip = await getUserLocation();

      if (!detectedZip) {
        console.log('No ZIP detected');

        setManualError('Unable to detect location');
        setHasManualError(true);

        formik.setFieldError('zipCode', 'Unable to detect location');
        formik.setFieldTouched('zipCode', true, false);

        return;
      }

      const isValidUSZip = /^\d{5}$/.test(detectedZip);

      await formik.setFieldValue('zipCode', detectedZip);

      if (isValidUSZip) {
        setManualError(null);
        setHasManualError(false);
        formik.setErrors({});
        formik.setTouched({ zipCode: false });

        await fetchDealersListByZipCode(detectedZip);
      } else {
        setManualError(errorMessage);
        setHasManualError(true);

        formik.setFieldError('zipCode', errorMessage);
        formik.setFieldTouched('zipCode', true, true);
      }
    } catch (error) {
      console.log('Location error:', error);
      setManualError('Unable to detect location');
      setHasManualError(true);

      formik.setFieldError('zipCode', 'Unable to detect location');
      formik.setFieldTouched('zipCode', true, true);
    }
  };

  const displayError = manualError || (formik.touched.zipCode ? formik.errors.zipCode : undefined);
  const hasError = hasManualError || (formik.touched.zipCode && !!formik.errors.zipCode);

  return (
    <StyledZipInputContainer>
      <StyledInputWrapper>
        <TextInput
          label={translationHandler('manageDealer.zipInput.label')}
          placeholder={translationHandler('manageDealer.zipInput.placeHolder')}
          name="zipCode"
          value={formik.values.zipCode}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          appearance={hasError ? 'error' : isLoadingDealers || isLoadingPreferredDealer ? 'disabled' : 'default'}
          message={hasError ? displayError : undefined}
        />

        <StyledLocationButton
          onClick={handleUseMyLocation}
          disabled={isLoadingDealers}
          type="button"
          aria-label="Use my location"
        >
          <Locate variant="default" />
        </StyledLocationButton>
      </StyledInputWrapper>
    </StyledZipInputContainer>
  );
};
